import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './hooks/useAppSelector';
import { loginSuccess, logout, setAppSyncUser } from '../../store/slices/authSlice';
import { openAuthModal, closeAuthModal } from '../../store/slices/uiSlice';
import { configureAppSync, isConfigured } from '../../services/config/appSync';
import { useUser } from '../../services/auth/hooks/useUser';
import { useAuth } from '../../services/auth/hooks/signUp';
import { useTokenRefresh } from '../../services/auth/hooks/useTokenRefresh';
import { getMergedUserData } from '../../services/auth/utils/getMergedUserData';
import { Header, LandingPage } from '../../components/index';
import { V2App } from '../../v2';
import AuthModal from '../../services/auth/components/AuthModal/AuthModal';
import { WebSocketProvider } from '../../services/websocket/context/WebSocketContext';
import './App.css';

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, userProfile, appSyncUser } = useAppSelector((state) => state.auth);
  const { isAuthModalOpen, authModalMode } = useAppSelector((state) => state.ui);
  
  // Loading state when checking tokens
  const [isCheckingTokens, setIsCheckingTokens] = useState(true);
  
  // Ensure Amplify is configured (should already be configured in index.tsx, but double-check)
  useEffect(() => {
    if (!isConfigured()) {
      console.warn('⚠️ Amplify not configured, attempting to configure now...');
      configureAppSync();
    }
  }, []); // Run only once on mount
  
  const { updateUser, updateLastSeen } = useUser();
  const { signUp: cognitoSignUp, signIn: cognitoSignIn, confirmSignUp: cognitoConfirmSignUp, resendConfirmationCode: cognitoResendCode, signOut: cognitoSignOut, refreshTokens } = useAuth();
  
  // Enable automatic token refresh when user is logged in
  // Only enable if Amplify is configured
  useTokenRefresh(refreshTokens, isLoggedIn && isConfigured());

  // Auth modal state
  const [tempSignupData, setTempSignupData] = useState<{
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const [pendingVerificationMode, setPendingVerificationMode] = useState<'login' | 'signup' | null>(null);
  const [userCreationInProgress, setUserCreationInProgress] = useState<Set<string>>(new Set());
  const [authInfoMessage, setAuthInfoMessage] = useState<string | null>(null);
  
  // Redirect to the new app when logged in and on root path, or to home when logged out
  // Don't redirect if already on a valid dashboard route
  // Skip redirect during token check to avoid interfering with route preservation
  useEffect(() => {
    // Don't run redirects while checking tokens
    if (isCheckingTokens) {
      return;
    }
    
    if (isLoggedIn && location.pathname === '/') {
      navigate('/feed', { replace: true });
    } else if (!isLoggedIn) {
      // Only redirect away from dashboard routes if not logged in
      const validAppRoutes = ['/feed', '/trips', '/ask-ai', '/explore', '/messages', '/profile', '/calendar', '/settings'];
      const isTripDetailRoute = /^\/trips\/[^/]+$/.test(location.pathname);
      if (validAppRoutes.includes(location.pathname) || isTripDetailRoute) {
        navigate('/', { replace: true });
      }
    }
  }, [isLoggedIn, location.pathname, navigate, isCheckingTokens]);

  // Initialize app on mount - check tokens and auto-login if valid
  useEffect(() => {
    // Only initialize if Amplify is configured
    if (isConfigured() && !isLoggedIn) {
      initializeApp();
    }
  }, []); // Run only once on mount
  
  // Re-initialize when dependencies change (if not already logged in)
  useEffect(() => {
    if (isConfigured() && !isLoggedIn) {
      initializeApp();
    }
  }, [updateUser, updateLastSeen]);

  const initializeApp = async () => {
    try {
      setIsCheckingTokens(true);
      
      // Save the original pathname before any redirects happen
      const originalPathname = window.location.pathname;
      
      // Ensure Amplify is configured (should already be done, but double-check)
      if (!isConfigured()) {
        const configured = configureAppSync();
        if (!configured) {
          console.warn('⚠️ Amplify configuration failed, skipping app initialization');
          setIsCheckingTokens(false);
          return;
        }
      }

      // Check if tokens are present and not expired
      // If tokens are valid, fetch user data and auto-login
      console.log('🔄 Checking for valid tokens on main page load...');
      
      const mergedUserData = await getMergedUserData();
      
      if (mergedUserData) {
        console.log('✅ Valid tokens found, fetching user data and logging in...');
        
        // Convert merged data to AppSyncUser format
        // All user data (except email/email_verified) comes from AppSync
        const appSyncUser: any = {
          id: mergedUserData.id || mergedUserData.sub,
          email: mergedUserData.email,
          firstName: mergedUserData.firstName,
          lastName: mergedUserData.lastName,
          nickname: mergedUserData.nickname,
          birthDate: mergedUserData.birthdate,
          avatarUrl: mergedUserData.avatarUrl,
          picture_url: mergedUserData.picture_url,
          lastSeenAt: mergedUserData.lastSeenAt,
          createdAt: mergedUserData.createdAt || new Date().toISOString(),
        };
        
        // Set user data in Redux
        dispatch(setAppSyncUser(appSyncUser));
        
        // Login user - this will automatically redirect to Dashboard
        dispatch(loginSuccess({
          id: 1,
          name: appSyncUser.firstName || 'User',
          email: appSyncUser.email
        }));
        
        console.log('✅ User logged in automatically with valid tokens, redirecting to dashboard');
        
        // Preserve the original route if it's a valid app route, otherwise go to feed
        const validAppRoutes = ['/feed', '/trips', '/ask-ai', '/explore', '/messages', '/profile', '/profile/trips', '/calendar', '/settings'];
        const isTripDetailRoute = /^\/trips\/[^/]+$/.test(originalPathname);
        
        if (originalPathname === '/' || (!validAppRoutes.includes(originalPathname) && !isTripDetailRoute)) {
          navigate('/feed', { replace: true });
        } else {
          // Preserve the original route by navigating to it (this ensures React Router knows about it)
          navigate(originalPathname, { replace: true });
        }
      } else {
        console.log('⚠️ No valid tokens found, user must log in');
        // No valid tokens, user stays on landing page
      }
    } catch (error) {
      console.error('Failed to initialize app:', error);
    } finally {
      setIsCheckingTokens(false);
    }
  };

  const handleLoginClick = () => {
    dispatch(openAuthModal('login'));
  };

  const handleSignUpClick = () => {
    dispatch(openAuthModal('signup'));
  };

  const handleCloseAuthModal = () => {
    dispatch(closeAuthModal());
    setTempSignupData(null);
    setPendingVerificationEmail(null);
    setPendingVerificationMode(null);
    setUserCreationInProgress(new Set());
    setAuthInfoMessage(null);
  };

  const handleSignUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      await cognitoSignUp(email, password, firstName, lastName);
      setTempSignupData({ email, password, firstName: firstName || '', lastName: lastName || '' });
      setPendingVerificationEmail(email);
      setPendingVerificationMode('signup');
    } catch (error: any) {
      if (error.name === 'UsernameExistsException') {
        await cognitoResendCode(email);
        setTempSignupData({ email, password, firstName: firstName || '', lastName: lastName || '' });
        setPendingVerificationEmail(email);
        setPendingVerificationMode('signup');
        return;
      }
      throw error;
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      // Dev testing mode - ONLY enabled in development
      // This bypass allows testing without Cognito but is a security risk in production
      if (process.env.NODE_ENV === 'development' && email === 'test' && password === 'test123') {
        console.warn('⚠️ Using test login bypass - DEVELOPMENT MODE ONLY');
        const testUser = {
          id: 'test-user-id',
          email: 'test@example.com',
          firstName: 'Test',
          lastName: 'User',
          lastSeenAt: new Date().toISOString(),
          createdAt: new Date().toISOString()
        };
        
        dispatch(setAppSyncUser(testUser));
        dispatch(loginSuccess({
          id: 1,
          name: 'Test User',
          email: 'test@example.com'
        }));
        dispatch(closeAuthModal());
        return;
      }

      await cognitoSignIn(email, password);
      
      if (userCreationInProgress.has(email)) {
        return;
      }

      setUserCreationInProgress(prev => new Set(prev).add(email));

      await new Promise(resolve => setTimeout(resolve, 500));

      dispatch(loginSuccess({
        id: 1,
        name: tempSignupData?.firstName || 'User',
        email: email
      }));

      setTempSignupData(null);
      setUserCreationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
      
      dispatch(closeAuthModal());
      
      // Navigate to the new app after successful login
      // Preserve current route if it's a valid app route, otherwise go to feed
      const currentPath = location.pathname;
      const validAppRoutes = ['/feed', '/trips', '/ask-ai', '/explore', '/messages', '/profile', '/profile/trips', '/calendar', '/settings'];
      const isTripDetailRoute = /^\/trips\/[^/]+$/.test(currentPath);
      
      if (currentPath === '/' || (!validAppRoutes.includes(currentPath) && !isTripDetailRoute)) {
        navigate('/feed');
      } else {
        // Already on a valid route, no need to navigate
      }
    } catch (error: any) {
      setUserCreationInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });

      // User doesn't exist - switch to signup modal
      if (error.name === 'UserNotFoundException') {
        console.log('User does not exist, switching to signup mode');
        setAuthInfoMessage('This account doesn\'t exist yet. Please create an account to continue.');
        dispatch(openAuthModal('signup'));
        // Throw a special error to prevent modal from closing
        // AuthModal will catch this and keep the modal open
        throw new Error('SWITCH_TO_SIGNUP');
      }

      if (error.name === 'UserNotConfirmedException') {
        setPendingVerificationEmail(email);
        setPendingVerificationMode('login');
        throw new Error('Please verify your email address. We\'ve sent you a verification code.');
      }
      throw error;
    }
  };

  const handleConfirmSignUp = async (email: string, verificationCode: string) => {
    await cognitoConfirmSignUp(email, verificationCode);
    
    if (tempSignupData && tempSignupData.email === email) {
      await handleSignIn(tempSignupData.email, tempSignupData.password);
      setTempSignupData(null);
    }
  };

  const handleResendCode = async (email: string) => {
    await cognitoResendCode(email);
  };

  const handleLogout = async () => {
    try {
      await cognitoSignOut();
      dispatch(logout());
      setTempSignupData(null);
      setPendingVerificationEmail(null);
      setPendingVerificationMode(null);
      setUserCreationInProgress(new Set());
      // Navigate to home page after logout
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      dispatch(logout());
      navigate('/', { replace: true });
    }
  };

  // Show loading state while checking tokens
  if (isCheckingTokens && !isLoggedIn) {
    return (
      <div className="app">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#666', fontSize: '16px' }}>Checking authentication...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <WebSocketProvider>
      <div className="app">
        <Routes>
        {/* Home/Landing Page Route */}
        <Route 
          path="/" 
          element={
            <>
              <main className="main-content">
                {isLoggedIn ? <Navigate to="/feed" replace /> : (
                  <LandingPage 
                    onLoginClick={handleLoginClick}
                    onSignUpClick={handleSignUpClick}
                  />
                )}
              </main>
            </>
          }
        />

        {/* New app owns direct authenticated routes */}
        <Route
          path="/*"
          element={
            isLoggedIn ? (
              <V2App />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        
        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Auth Modal */}
      {isAuthModalOpen && createPortal(
        <AuthModal
          mode={authModalMode}
          onClose={handleCloseAuthModal}
          signUp={handleSignUp}
          signIn={handleSignIn}
          confirmSignUp={handleConfirmSignUp}
          resendCode={handleResendCode}
          pendingVerificationEmail={pendingVerificationEmail}
          pendingVerificationMode={pendingVerificationMode}
          infoMessage={authInfoMessage}
        />,
        document.body
      )}
      </div>
    </WebSocketProvider>
  );
};

export default App;